/**
 * Registered ECharts themes for Exec widgets.
 *
 * Reads OKLCH semantic tokens from CSS variables on `document.documentElement`
 * and builds two themes — `overmatch-light` and `overmatch-dark` — that match
 * the Tailwind palette used elsewhere in the app. Renderers should pass the
 * theme name (via `useExecChartTheme()`) into `<ReactECharts theme={...} />`.
 *
 * a11y defaults are on by default (`aria.enabled` + `decal.show`) so charts
 * are screen-reader friendly and color-blind safe without per-renderer opt-in.
 */
import * as echarts from 'echarts/core'
import { useEffect, useState } from 'react'

const LIGHT_THEME_NAME = 'overmatch-light'
const DARK_THEME_NAME = 'overmatch-dark'

/** Read a CSS variable value from :root, with a fallback if not set. */
function readVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

/** Build a theme object from current CSS-var values. */
function buildTheme(mode: 'light' | 'dark') {
  // Chart palette — read in order so categorical series get a stable, accessible cycle.
  const palette = [
    readVar('--chart-1', mode === 'dark' ? '#7d8cff' : '#e0671a'),
    readVar('--chart-2', mode === 'dark' ? '#6fbf9c' : '#3a8fa1'),
    readVar('--chart-3', mode === 'dark' ? '#dc9433' : '#1f5d8a'),
    readVar('--chart-4', mode === 'dark' ? '#aa6dff' : '#dfaa3a'),
    readVar('--chart-5', mode === 'dark' ? '#df5b5b' : '#d29c3a'),
    // Extend the palette beyond the five chart-* tokens so multi-series widgets
    // (e.g., 8(a)/HUBZone/SDVOSB/WOSB stacked bars) don't wrap onto chart-1 again.
    readVar('--primary', mode === 'dark' ? '#7d99cc' : '#3370b3'),
    readVar('--success', '#7ab87a'),
    readVar('--warning', '#dfb968'),
    readVar('--destructive', mode === 'dark' ? '#cc6666' : '#b94a4a'),
    '#a78bfa', // purple — the only hand-chosen extension; safe in both modes
  ]

  const fg = readVar('--foreground', mode === 'dark' ? '#e8e6e3' : '#27272a')
  const muted = readVar('--muted-foreground', mode === 'dark' ? '#a1a1aa' : '#71717a')
  const border = readVar('--border', mode === 'dark' ? '#3f3f46' : '#e4e4e7')
  const bg = readVar('--background', mode === 'dark' ? '#1c1c1f' : '#fafafa')

  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: { color: fg, fontFamily: "'Aeonik Pro', system-ui, -apple-system, sans-serif" },
    title: { textStyle: { color: fg }, subtextStyle: { color: muted } },
    legend: { textStyle: { color: muted, fontSize: 11 } },
    grid: { borderColor: border },
    categoryAxis: {
      axisLine: { lineStyle: { color: border } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: muted, fontSize: 10 },
      splitLine: { lineStyle: { color: border, type: 'dashed', opacity: 0.4 } },
    },
    valueAxis: {
      axisLine: { lineStyle: { color: border } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: muted, fontSize: 10 },
      splitLine: { lineStyle: { color: border, type: 'dashed', opacity: 0.4 } },
    },
    timeAxis: {
      axisLine: { lineStyle: { color: border } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: muted, fontSize: 10 },
      splitLine: { lineStyle: { color: border, type: 'dashed', opacity: 0.4 } },
    },
    logAxis: {
      axisLine: { lineStyle: { color: border } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: muted, fontSize: 10 },
      splitLine: { lineStyle: { color: border, type: 'dashed', opacity: 0.4 } },
    },
    tooltip: {
      backgroundColor: bg,
      borderColor: border,
      textStyle: { color: fg, fontSize: 11 },
      extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); border-radius: 6px;',
    },
    visualMap: {
      textStyle: { color: muted, fontSize: 10 },
    },
    dataZoom: {
      borderColor: border,
      fillerColor: mode === 'dark' ? 'rgba(125, 140, 255, 0.15)' : 'rgba(224, 103, 26, 0.12)',
      handleStyle: { color: palette[0] },
      textStyle: { color: muted, fontSize: 10 },
    },
    bar: {
      itemStyle: { borderRadius: [2, 2, 0, 0] },
    },
    line: {
      itemStyle: { borderWidth: 2 },
      lineStyle: { width: 2 },
      symbolSize: 6,
      symbol: 'circle',
      smooth: false,
    },
    // a11y: screen-reader text + color-blind decals globally.
    aria: {
      enabled: true,
      decal: { show: true },
    },
  }
}

let registered = false

/** Register both themes on the echarts singleton. Idempotent. */
function registerThemes() {
  if (registered) return
  echarts.registerTheme(LIGHT_THEME_NAME, buildTheme('light'))
  echarts.registerTheme(DARK_THEME_NAME, buildTheme('dark'))
  registered = true
}

/** Detect dark mode by looking at the `dark` class on the html element. */
function isDarkMode(): boolean {
  if (typeof document === 'undefined') return false
  return document.documentElement.classList.contains('dark')
}

/**
 * React hook returning the current theme name. Re-renders when the user toggles
 * light/dark via ThemeToggle (observes the `class` attribute on `html`).
 */
export function useExecChartTheme(): string {
  registerThemes()
  const [name, setName] = useState<string>(isDarkMode() ? DARK_THEME_NAME : LIGHT_THEME_NAME)

  useEffect(() => {
    const html = document.documentElement
    const observer = new MutationObserver(() => {
      // Re-register so the themes pick up any new CSS-var values (e.g., on theme switch
      // the chart-* tokens differ between light/dark, so we want fresh values).
      registered = false
      registerThemes()
      setName(isDarkMode() ? DARK_THEME_NAME : LIGHT_THEME_NAME)
    })
    observer.observe(html, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  return name
}

/** Synchronous accessor for non-hook contexts (e.g., one-off chart renders). */
export function getExecChartTheme(): string {
  registerThemes()
  return isDarkMode() ? DARK_THEME_NAME : LIGHT_THEME_NAME
}

/**
 * Build an `onChartReady` callback that assigns the given group to the chart
 * instance so it joins the connect-set declared at the dashboard level.
 *
 * Phase F1: sync hover crosshair + dataZoom across widgets that share a group.
 * No-op when `group` is empty (e.g., on `/exec/explore`'s single-chart page).
 */
export function makeChartReady(group?: string) {
  return (chart: any) => {
    if (group && chart) {
      chart.group = group
    }
  }
}

/**
 * Idempotent connect for a dashboard's chart group. Safe to call multiple
 * times — internally `echarts.connect` is a single-effect on the group.
 */
export function connectChartGroup(group: string) {
  if (!group) return
  echarts.connect(group)
}
